import { FC, useCallback, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import BasicField from '@payaca/components/basicField/BasicField';
import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';
import Modal from '@payaca/components/modal/Modal';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';

import FeedbackBlock from '@payaca/components/feedbackBlock/FeedbackBlock';
import { setChangeProposalDiscount } from '@payaca/store/proposals/proposalsActions';
import { ChangeProposal } from '@payaca/types/changeProposalTypes';
import { FeedbackLevel } from '@payaca/types/feedbackTypes';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { isNullish } from '@payaca/utilities/guards';
import './SetChangeProposalDiscountModal.sass';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  changeProposalId: ChangeProposal['id'];
  onSetChangeProposalDiscountSuccess?: () => void;
};

const initialFormState = {};

const SetChangeProposalDiscountModal: FC<Props> = ({
  isOpen,
  onClose,
  changeProposalId,
  onSetChangeProposalDiscountSuccess,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const [isProcessing, setIsProcessing] = useState(false);

  const onSubmit = useCallback(
    (discountPercentage: number, discountDescription?: string) => {
      setIsProcessing(true);
      dispatch(
        setChangeProposalDiscount.request({
          changeProposalId: changeProposalId,
          discountPercentage,
          discountDescription,
          callback: () => {
            setIsProcessing(false);
            onClose();
            onSetChangeProposalDiscountSuccess?.();
          },
          onErrorCallback: () => {
            setIsProcessing(false);
          },
        })
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'dispatch', 'onClose', and 'onSetChangeProposalDiscountSuccess'
    [changeProposalId]
  );

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      return (
        <>
          <div className="set-change-proposal-discount-form-contents">
            <BasicField
              label="Discount percentage"
              name="discountPercentage"
              value={formState.discountPercentage}
              onChange={(change: { [key: string]: any }) =>
                onFieldChange({
                  discountPercentage: change.discountPercentage
                    ? Math.round(change.discountPercentage * 100) / 100
                    : null,
                })
              }
              iconAfter={'percent-02.3'}
              className="discount-percentage"
              type="number"
              additionalInputProps={{
                pattern: '[0-9]*',
                min: 0,
                max: 100,
                step: 1,
              }}
            />
            <BasicField
              label="Reason for discount"
              name="discountDescription"
              value={formState.discountDescription}
              onChange={onFieldChange}
            />
            <FeedbackBlock feedbackLevel={FeedbackLevel.ALERT}>
              Any existing Item discounts will be overwritten
            </FeedbackBlock>
            <div className="actions-container">
              <Button
                styleVariant={ButtonStyleVariant.OUTSIZE}
                isProcessing={isProcessing}
                isDisabled={isNullish(formState.discountPercentage)}
                onClick={() =>
                  onSubmit(
                    formState.discountPercentage,
                    formState.discountDescription
                  )
                }
              >
                Set{' '}
                {isNullish(formState.discountPercentage) ? (
                  ''
                ) : (
                  <>{formState.discountPercentage}% </>
                )}
                discount
              </Button>
            </div>
          </div>
        </>
      );
    },
    [isProcessing, onSubmit]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Set a discount"
      className="set-change-proposal-discount-modal"
    >
      <ValidatedForm
        initialFormState={initialFormState}
        renderFormContents={renderFormContents}
      />
    </Modal>
  );
};
export default SetChangeProposalDiscountModal;
